import type { Patient, Reference } from '@medplum/fhirtypes'
import { featureFlags } from '@seen/core/featureFlags'
import { invariant } from '@seen/core/invariant'
import { InitialCarePlan } from '@seen/core/models/care-plans/InitialCarePlan'
import { NoteTemplate } from '@seen/core/models/noteTemplate'
import { Button } from '@seen/ui'
import { IconPlus } from '@tabler/icons-react'
import type { JSX } from 'react'
import { DropdownMenu } from '#src/components/core/dropdown-menu/DropdownMenu.tsx'
import { DropdownMenuContent } from '#src/components/core/dropdown-menu/DropdownMenuContent.tsx'
import { DropdownMenuItem } from '#src/components/core/dropdown-menu/DropdownMenuItem.tsx'
import { DropdownMenuTrigger } from '#src/components/core/dropdown-menu/DropdownMenuTrigger.tsx'
import { TemplateCombobox } from '#src/components/TemplateCombobox.tsx'
import {
  type CreateCarePlanParams,
  useCreateCarePlan,
} from '#src/hooks/useCreateCarePlan.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { contentToHTML } from '#src/utils/noteContent.ts'

const CARE_PLAN_CREATE_TYPES = [
  'INITIAL',
  'SEMI_ANNUAL',
  'CHANGE_OF_CARE',
] as const satisfies readonly (keyof typeof InitialCarePlan.TYPES)[]

export interface CreateCarePlanButtonProps {
  defaultOpen?: boolean
  participant: Reference<Patient>
  onCreated: (carePlanId: string) => void
}

export function CreateCarePlanButton({
  defaultOpen,
  participant,
  onCreated,
}: CreateCarePlanButtonProps): JSX.Element {
  const typedCreateEnabled = useFeatureFlag(featureFlags.carePlanTypedCreate)
  const templateCreateEnabled = useFeatureFlag(
    featureFlags.carePlanTemplateCreate
  )
  const { createCarePlan, isCreatingCarePlan } = useCreateCarePlan()

  return (
    <CreateCarePlanButtonView
      defaultOpen={defaultOpen}
      participant={participant}
      onCreated={onCreated}
      typedCreateEnabled={typedCreateEnabled}
      templateCreateEnabled={templateCreateEnabled}
      createCarePlan={createCarePlan}
      isCreatingCarePlan={isCreatingCarePlan}
    />
  )
}

export interface CreateCarePlanButtonViewProps extends CreateCarePlanButtonProps {
  typedCreateEnabled: boolean
  templateCreateEnabled: boolean
  createCarePlan: (params: CreateCarePlanParams) => void
  isCreatingCarePlan: boolean
}

export function CreateCarePlanButtonView({
  defaultOpen,
  participant,
  onCreated,
  typedCreateEnabled,
  templateCreateEnabled,
  createCarePlan,
  isCreatingCarePlan,
}: CreateCarePlanButtonViewProps): JSX.Element {
  const created = (carePlan: { id: string }): void => {
    onCreated(carePlan.id)
  }

  if (typedCreateEnabled) {
    return (
      <DropdownMenu defaultOpen={defaultOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" loading={isCreatingCarePlan}>
            Create Care Plan
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {CARE_PLAN_CREATE_TYPES.map((type) => {
            const display = InitialCarePlan.TYPES[type].coding?.[0]?.display
            invariant(display, `Care plan type ${type} must have a display`)
            return (
              <DropdownMenuItem
                key={type}
                onClick={() =>
                  createCarePlan({
                    participant,
                    type,
                    onSuccess: created,
                  })
                }
              >
                {display}
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  if (templateCreateEnabled) {
    return (
      <TemplateCombobox
        templateClass={NoteTemplate}
        onSelect={(template) => {
          createCarePlan({
            participant,
            description: contentToHTML(template.data),
            onSuccess: created,
          })
        }}
      >
        <Button
          variant="outline"
          className="flex items-center gap-2"
          loading={isCreatingCarePlan}
        >
          <IconPlus className="size-4" />
          Create Care Plan…
        </Button>
      </TemplateCombobox>
    )
  }

  return (
    <Button
      variant="outline"
      loading={isCreatingCarePlan}
      onClick={() =>
        createCarePlan({
          participant,
          onSuccess: created,
        })
      }
    >
      Create Care Plan
    </Button>
  )
}
